import { SeoLandingLayout, type SeoPageContent } from '@/components/seo/SeoLandingLayout'

const content: SeoPageContent = {
  locale: 'en',
  path: '/co-parenting-expense-sharing',
  alternatePath: '/co-parenting-ausgaben-teilen',
  seo: {
    title: 'Co-parenting — split child expenses fairly with Equna',
    description:
      'Equna helps separated parents log child-related costs transparently, store receipts and split by a shared rule — no arguments, no spreadsheets.',
  },
  hero: {
    eyebrow: 'For co-parents',
    h1: 'Split child expenses fairly — even when you live apart',
    sub: 'School, clothes, hobbies, doctor visits: Equna gathers everything in one place, attaches receipts and splits by your rule. No more debates, no gut-feeling math — just numbers both parents can see.',
    note: 'Start free. Your data stays in Germany.',
  },
  problems: {
    heading: 'Why child expenses turn into a burden quickly',
    intro:
      'After a separation, there are many new topics — and money is one of the hardest. Exactly when you need a shared place where expenses are cleanly documented.',
    items: [
      {
        title: 'Receipts everywhere',
        description:
          'Paper receipt from school supplies, email from the sports club, photo of a doctor invoice on WhatsApp — all in different places.',
      },
      {
        title: 'Who covered what?',
        description:
          'Without clean documentation, memories diverge — and small disagreements grow fast.',
      },
      {
        title: 'Different rules per category',
        description:
          'Clothes 50/50, school proportional, hobbies by income — keeping all of that in your head does not work.',
      },
      {
        title: 'No clean cutoff',
        description:
          'Without a monthly close, the imbalance lingers. Parents feel debts they cannot put numbers on.',
      },
    ],
  },
  solutions: {
    heading: 'What Equna does for co-parents',
    intro:
      'One app, both parents, the same view. Expenses get logged the moment they happen — by month-end the balance is clear.',
    items: [
      {
        icon: 'kids',
        title: 'Expenses per child',
        description:
          'More than one child? Assign each expense to the right child — clean per-child analysis is possible.',
      },
      {
        icon: 'receipt',
        title: 'Receipt attached on the spot',
        description:
          'Photo of the paper receipt, screenshot of the e-invoice. Documentation happens the moment money flows.',
      },
      {
        icon: 'split',
        title: 'Your split rule',
        description:
          '50/50, income-proportional or custom. Once agreed, always applied.',
      },
      {
        icon: 'calendar',
        title: 'Monthly close as a cutoff',
        description:
          'Both parents confirm the month. Equna computes who transfers what. Clear line, then back to zero.',
      },
      {
        icon: 'shield',
        title: 'Factual, not emotional',
        description:
          'Instead of arguments in chat, you have numbers in front of you. Both parents see the same state and history.',
      },
      {
        icon: 'check',
        title: 'Documented for dispute cases',
        description:
          'Should disagreements come up, every amount has a date and receipt — no inbox archeology needed.',
      },
    ],
  },
  comparison: {
    heading: 'Before vs. with Equna',
    columns: ['Without an app', 'With Equna'],
    rows: [
      {
        topic: 'Logging child expenses',
        without: 'Paper notes, chat messages, spreadsheets',
        with: '10-second entry, both parents see it',
      },
      {
        topic: 'Receipts',
        without: 'Scattered across email, photo gallery, bag',
        with: 'Stored right on the expense',
      },
      {
        topic: 'Split rule',
        without: 'Debated per expense',
        with: 'Defined once, applied automatically',
      },
      {
        topic: 'Multiple children',
        without: 'Manual filtering across lists',
        with: 'Direct per-child assignment and analysis',
      },
      {
        topic: 'Monthly cutoff',
        without: 'Rarely happens',
        with: 'Both confirm, app computes the balance',
      },
    ],
  },
  trust: {
    heading: 'Why Equna fits co-parents',
    items: [
      {
        title: 'Made in Germany',
        description: 'Servers in Germany, operator in Berlin. Your data does not leave the EU.',
      },
      {
        title: 'GDPR-compliant',
        description: 'Receipts and expenses stay with you — no sharing with ad networks.',
      },
      {
        title: 'Both parents equal',
        description: 'No main user, no add-on. Both accounts have the same rights.',
      },
      {
        title: 'Free to start',
        description: 'The free tier covers all co-parenting features — no time limit.',
      },
    ],
  },
  relatedBlog: {
    heading: 'Read more',
    posts: [
      {
        title: 'Split household costs — five models',
        description: 'Which split model fits your setup? Four examples and a recommendation.',
        href: '/blog/split-household-costs-five-models',
      },
      {
        title: 'Equna vs. Splitwise',
        description: 'Why Splitwise is strong for one-off amounts and Equna is built for ongoing co-parenting costs.',
        href: '/blog/equna-vs-splitwise-for-couples-and-families',
      },
      {
        title: '3-account model and Equna',
        description: 'How a shared account for child costs works together with Equna.',
        href: '/blog/3-account-model-and-equna-why-they-belong-together',
      },
    ],
  },
  faq: {
    heading: 'Common co-parent questions',
    items: [
      {
        question: 'Do both parents need to use Equna?',
        answer:
          "It makes sense for both to have the app — then both see the same numbers in real time. You can also start solo and invite the other parent later.",
      },
      {
        question: 'How does it work with multiple children?',
        answer:
          'You add each child once and assign every expense to a child. That way you get clean per-child analysis — useful for taxes, child support or family conversations.',
      },
      {
        question: 'Can we use different split rules per area?',
        answer:
          'There is one global rule that applies to all expenses. On individual expenses you can override the split — for instance if only one parent covers a specific purchase.',
      },
      {
        question: 'Can I export the data — e.g. for a lawyer?',
        answer:
          'Yes. You can download a GDPR data export. In dispute cases you have a traceable history with date, amount and receipt.',
      },
      {
        question: 'Is Equna free?',
        answer:
          'Yes. Co-parenting features like per-child expenses, receipts (60 days) and monthly close are free. An optional premium tier with longer receipt storage is available later.',
      },
    ],
  },
  finalCta: {
    heading: 'Clear child expenses — no fights over small things',
    sub: 'Install Equna, invite the other parent, set the split.',
  },
  uiStrings: {
    backToHome: '← Back to home',
    related: 'Read more',
    legalPrivacy: 'Privacy',
    legalTerms: 'Terms',
    legalImprint: 'Imprint',
    legalSupport: 'Support',
    copyrightRights: 'All rights reserved.',
    breadcrumbHome: 'Home',
    breadcrumbCurrent: 'Co-parenting expense sharing',
  },
}

export default function CoParentingSeoPageEn() {
  return <SeoLandingLayout content={content} />
}
